import React, { useState } from 'react';
import { ISavRepairerQuote } from '../../../../../../../../../models/savRepairerQuote';
import { IVoucherAmountDetail } from '../../../../../../../../../models/sav';
import { formatDate } from '../../utils/dates';

interface QuoteViewerProps {
  quote: ISavRepairerQuote | null;
  amountDetails?: IVoucherAmountDetail[];
  className?: string;
  onSelectLine?: (lineIndex: number, isSelected: boolean) => void;
}

/**
 * Composant qui affiche les détails du devis réparateur
 * Utilisé pour consulter et sélectionner les lignes du devis dans l'étape de qualification
 */
const QuoteViewer: React.FC<QuoteViewerProps> = ({
  quote,
  amountDetails = [],
  className = '',
  onSelectLine
}) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedLine, setExpandedLine] = useState<number | null>(null);

  if (!quote) {
    return (
      <div className={`tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200 ${className}`}>
        <div className="tw-p-3 tw-text-center">
          <h3 className="tw-text-lg tw-font-medium tw-text-gray-600">Devis réparateur</h3>
          <p className="tw-text-gray-500">Aucun devis disponible</p>
        </div>
      </div>
    );
  }

  // Fonction pour trier les lignes du devis
  const sortedAmountDetails = [...amountDetails].sort((a, b) => {
    if (!sortField) return 0;

    const valueA = a[sortField as keyof IVoucherAmountDetail];
    const valueB = b[sortField as keyof IVoucherAmountDetail];

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortDirection === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    } else if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    }

    return 0;
  });

  // Fonction pour gérer le clic sur l'en-tête de colonne (tri)
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Fonction pour gérer la sélection d'une ligne
  const handleLineSelection = (index: number, isSelected: boolean) => {
    if (onSelectLine) {
      onSelectLine(index, isSelected);
    }
  };

  // Calcul des totaux
  const totalHT = amountDetails.reduce((sum, detail) => sum + detail.amountTotalHT, 0);
  const totalTTC = amountDetails.reduce((sum, detail) => sum + detail.amountTotalTTC, 0);

  return (
    <div className={`tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm ${className}`}>
      <div className="tw-p-4 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-medium tw-flex tw-items-center tw-gap-2">
          <i className="fa-solid fa-file-invoice tw-text-blue-600"></i>
          Devis réparateur
        </h3>
      </div>

      <div className="tw-p-4">
        {/* Informations générales du devis */}
        <div className="tw-mb-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          <div>
            <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Informations du devis</h4>
            <dl className="tw-space-y-2">
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Réparateur</dt>
                <dd className="tw-font-medium tw-text-gray-800">{quote.company?.name || '-'}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Document</dt>
                <dd className="tw-font-medium tw-text-gray-800">{quote.document?.label || '-'}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Date</dt>
                <dd className="tw-font-medium tw-text-gray-800">{formatDate(quote.document?.dateCreation)}</dd>
              </div>
            </dl>
          </div>

          <div>
            <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Totaux</h4>
            <dl className="tw-space-y-2">
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Total HT</dt>
                <dd className="tw-font-medium tw-text-gray-800">{totalHT.toLocaleString('fr-FR')} €</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Total TTC</dt>
                <dd className="tw-font-medium tw-text-gray-800">{totalTTC.toLocaleString('fr-FR')} €</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Nombre d'éléments</dt>
                <dd className="tw-font-medium tw-text-gray-800">{amountDetails.length}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Actions sur le document */}
        <div className="tw-mb-4 tw-flex tw-gap-2">
          {quote.document?.downloadUrl && (
            <a
              href={quote.document.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-bg-blue-50 tw-text-blue-700 tw-rounded-md tw-text-sm hover:tw-bg-blue-100 tw-transition-colors"
            >
              <i className="fa-solid fa-download"></i>
              Télécharger le devis
            </a>
          )}
        </div>

        {/* Tableau des lignes du devis */}
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
            <thead className="tw-bg-gray-50">
              <tr>
                {onSelectLine && (
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-12">
                    Sel.
                  </th>
                )}
                <th
                  className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-cursor-pointer"
                  onClick={() => handleSort('ref')}
                >
                  <div className="tw-flex tw-items-center">
                    Réf.
                    {sortField === 'ref' && (
                      <i className={`fa-solid fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} tw-ml-1`}></i>
                    )}
                  </div>
                </th>
                <th
                  className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-cursor-pointer"
                  onClick={() => handleSort('label')}
                >
                  <div className="tw-flex tw-items-center">
                    Désignation
                    {sortField === 'label' && (
                      <i className={`fa-solid fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} tw-ml-1`}></i>
                    )}
                  </div>
                </th>
                <th
                  className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-cursor-pointer"
                  onClick={() => handleSort('quantity')}
                >
                  <div className="tw-flex tw-items-center">
                    Qté
                    {sortField === 'quantity' && (
                      <i className={`fa-solid fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} tw-ml-1`}></i>
                    )}
                  </div>
                </th>
                <th
                  className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-cursor-pointer"
                  onClick={() => handleSort('unitPrice')}
                >
                  <div className="tw-flex tw-items-center">
                    Prix unitaire
                    {sortField === 'unitPrice' && (
                      <i className={`fa-solid fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} tw-ml-1`}></i>
                    )}
                  </div>
                </th>
                <th
                  className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-cursor-pointer"
                  onClick={() => handleSort('amountTotalTTC')}
                >
                  <div className="tw-flex tw-items-center">
                    Total TTC
                    {sortField === 'amountTotalTTC' && (
                      <i className={`fa-solid fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} tw-ml-1`}></i>
                    )}
                  </div>
                </th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
              {sortedAmountDetails.length === 0 ? (
                <tr>
                  <td
                    colSpan={onSelectLine ? 7 : 6}
                    className="tw-px-3 tw-py-3 tw-text-sm tw-text-gray-500 tw-text-center"
                  >
                    Aucune ligne de devis disponible
                  </td>
                </tr>
              ) : (
                sortedAmountDetails.map((detail, index) => (
                  <React.Fragment key={index}>
                    <tr className={`hover:tw-bg-gray-50 ${expandedLine === index ? 'tw-bg-blue-50' : ''}`}>
                      {onSelectLine && (
                        <td className="tw-px-3 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={(e) => handleLineSelection(index, e.target.checked)}
                            className="tw-rounded"
                          />
                        </td>
                      )}
                      <td className="tw-px-3 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                        {detail.ref}
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-text-sm tw-text-gray-500">
                        {detail.label.length > 50
                          ? `${detail.label.substring(0, 50)}...`
                          : detail.label}
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                        {detail.quantity}
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                        {detail.unitPrice.toLocaleString('fr-FR')} €
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-700">
                        {detail.amountTotalTTC.toLocaleString('fr-FR')} €
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                        <button
                          onClick={() => setExpandedLine(expandedLine === index ? null : index)}
                          className="tw-text-blue-600 hover:tw-text-blue-800"
                        >
                          <i className={`fa-solid fa-chevron-${expandedLine === index ? 'up' : 'down'}`}></i>
                        </button>
                      </td>
                    </tr>
                    {expandedLine === index && (
                      <tr>
                        <td
                          colSpan={onSelectLine ? 7 : 6}
                          className="tw-px-3 tw-py-3 tw-bg-blue-50 tw-text-sm tw-text-gray-700"
                        >
                          <div className="tw-p-2">
                            <h5 className="tw-font-medium tw-mb-1">Détails de la ligne</h5>
                            <p className="tw-mb-2">{detail.label}</p>
                            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4">
                              <div>
                                <p className="tw-text-xs tw-text-gray-500">Prix unitaire HT</p>
                                <p>{detail.unitPrice.toLocaleString('fr-FR')} €</p>
                              </div>
                              <div>
                                <p className="tw-text-xs tw-text-gray-500">Total HT</p>
                                <p>{detail.amountTotalHT.toLocaleString('fr-FR')} €</p>
                              </div>
                              <div>
                                <p className="tw-text-xs tw-text-gray-500">Total TTC</p>
                                <p>{detail.amountTotalTTC.toLocaleString('fr-FR')} €</p>
                              </div>
                              <div>
                                <p className="tw-text-xs tw-text-gray-500">Référence</p>
                                <p>{detail.ref}</p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Info-bulle d'aide */}
        <div className="tw-mt-4 tw-bg-blue-50 tw-p-3 tw-rounded-lg tw-text-sm tw-text-blue-700 tw-flex tw-items-start tw-gap-2">
          <div className="tw-text-blue-600 tw-mt-0.5">
            <i className="fa-solid fa-circle-info"></i>
          </div>
          <div>
            <p>Ce tableau présente les lignes du devis réparateur.</p>
            {onSelectLine && (
              <p className="tw-mt-1">Sélectionnez les lignes à prendre en charge dans le cadre de la garantie.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteViewer;